// ─────────────────────────────────────────────────────────────────────────────
//  CellLookup — LookupField's behaviour, sized for a table cell
//
//  Used in the Insurance Coverage Records rows on Diagnosis and Procedure
//  setup, so Specialty / Code / Core can be picked from the same lists the
//  New Diagnosis / New Procedure dialogs offer instead of retyped by hand.
//
//    * click the arrow (or the box) and the full list drops down
//    * type to filter; matches that START with the text come first
//    * a value not in the list is still accepted and saved as typed
//
//  No label or helper text — the column header is the label. The list opens
//  wider than the cell, because specialty names ("OBSTETRICS AND
//  GYNAECOLOGY") do not fit a 120px column.
// ─────────────────────────────────────────────────────────────────────────────

import React from 'react';
import { Autocomplete, TextField, Box } from '@mui/material';

const filterStartsFirst = (opts, state) => {
  const q = (state.inputValue || '').trim().toLowerCase();
  if (!q) return opts;
  const starts = [], contains = [];
  for (const o of opts) {
    const s = String(o).toLowerCase();
    if (s.startsWith(q)) starts.push(o);
    else if (s.includes(q)) contains.push(o);
  }
  return [...starts, ...contains];
};

const CellLookup = ({ value, onChange, options = [], loading = false, sx = {}, popupMinWidth = 280 }) => (
  <Autocomplete
    freeSolo
    forcePopupIcon
    openOnFocus
    selectOnFocus
    clearOnBlur={false}
    handleHomeEndKeys
    size="small"
    fullWidth
    options={options}
    loading={loading}
    value={value || ''}
    onChange={(_e, next) => onChange(typeof next === 'string' ? next : (next ?? ''))}
    onInputChange={(_e, next, reason) => {
      if (reason === 'input') onChange(next);
    }}
    filterOptions={filterStartsFirst}
    renderOption={(props, option) => {
      const { key, ...rest } = props;
      return (
        <Box component="li" key={key} {...rest} sx={{ fontSize: '0.78rem' }}>
          {option}
        </Box>
      );
    }}
    noOptionsText={<Box sx={{ fontSize: '0.75rem' }}>No match — what you type is kept</Box>}
    slotProps={{
      popper: { placement: 'bottom-start', sx: { minWidth: popupMinWidth } },
      listbox: { style: { maxHeight: 260 } },
    }}
    sx={{
      '& .MuiOutlinedInput-root': {
        fontSize: '0.78rem',
        py: '0 !important',
        pl: '0 !important',
        '& input': { py: '4px !important', px: '8px !important' },
        '& fieldset': { borderColor: '#e0e0e0' },
        '&:hover fieldset': { borderColor: '#667eea' },
        '&.Mui-focused fieldset': { borderColor: '#667eea' },
      },
      '& .MuiAutocomplete-endAdornment': { right: '2px !important' },
      '& .MuiAutocomplete-popupIndicator': { p: '2px' },
      ...sx,
    }}
    renderInput={(params) => <TextField {...params} />}
  />
);

export default CellLookup;
